import { useCallback } from "react";

import { lockedActions } from "../../../../components/SettlementLock";
import { getCurrencySymbol, getNumberFormat } from "../../../../utils";
import { AccountChip } from "./AccountChip";
import { RowMenu } from "./RowMenu";
import {
  Amount,
  CategoryCell,
  CategoryDot,
  CategoryName,
  CategoryRow,
  DateText,
  DesktopRowWrapper,
  NoteCell,
  NoteText,
  SubCategoryName,
} from "./styles";
import { DesktopRowProps } from "./types";
import {
  formatRowDate,
  getAccountDotColor,
  getAmountSign,
  getAmountVariant,
  getPrimaryAccountId,
  getPrimaryAccountName,
  getSignedAmount,
} from "./utils";

export const DesktopRow: React.FC<DesktopRowProps> = ({
  transaction,
  accountIdsByName,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const accountName = getPrimaryAccountName(transaction);
  const accountId = getPrimaryAccountId(transaction);
  const variant = getAmountVariant(transaction.transactionType);
  const signedAmount = getSignedAmount(transaction);
  const symbol = getCurrencySymbol(transaction.currencyCode);
  const sign = getAmountSign(variant, transaction.amount);
  const amountLabel = `${sign}${getNumberFormat(Math.abs(signedAmount))} ${symbol}`;

  const handleEdit = useCallback(() => {
    onEdit(transaction);
  }, [onEdit, transaction]);

  const handleDuplicate = useCallback(() => {
    onDuplicate(transaction);
  }, [onDuplicate, transaction]);

  const handleDelete = useCallback(() => {
    onDelete(transaction);
  }, [onDelete, transaction]);

  const { lock, actions: menu } = lockedActions({
    settlement: transaction.settlement,
    cashbackExpenseId: transaction.cashbackExpenseId,
    actions: (
      <RowMenu
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
      />
    ),
    placeholder: <span />,
  });

  return (
    <DesktopRowWrapper data-testid="transaction-row">
      <DateText>{formatRowDate(transaction.transactionDate)}</DateText>
      <div>
        <AccountChip
          name={accountName ?? ""}
          accountId={accountId}
          accountIdsByName={accountIdsByName}
        />
      </div>
      <CategoryCell>
        <CategoryRow>
          {transaction.categoryName && (
            <CategoryDot
              $color={getAccountDotColor(transaction.categoryName)}
            />
          )}
          <CategoryName>{transaction.categoryName}</CategoryName>
        </CategoryRow>
        {transaction.subCategoryName && (
          <SubCategoryName>{transaction.subCategoryName}</SubCategoryName>
        )}
      </CategoryCell>
      <NoteCell>
        <NoteText title={transaction.note ?? ""}>
          {transaction.note ?? ""}
        </NoteText>
        {lock}
      </NoteCell>
      <Amount $variant={variant} $bold>
        {amountLabel}
      </Amount>
      {menu}
    </DesktopRowWrapper>
  );
};
